"use client";

import type { QUADRATIC_EQUATION_SOLVER_STRINGS } from "@/components/tools/quadratic-equation-solver";
import { t } from "@/lib/i18n/interpolate";
import { formatBig, isqrtBig } from "@/lib/tool-kit/math/bigint";
import {
  discriminantWorking,
  formatIntegerQuadratic,
  formatSurdPair,
  formulaSubstitution,
  type QuadraticSolution,
  rationalRootWorking,
  rootSimplification,
  squareWorking,
  unreducedSurd,
} from "@/lib/tool-kit/math/quadratic";
import { equalsR, formatFraction, intR } from "@/lib/tool-kit/math/rational";

/*
 * The two worked solutions of the quadratic solver: the quadratic formula (on the
 * whole-number form of the equation) and completing the square (on the equation as
 * entered, in exact fractions). Every sentence comes from the widget's strings.
 */

type Strings = typeof QUADRATIC_EQUATION_SOLVER_STRINGS;

const STEP_LIST =
  "list-decimal space-y-2 rounded-lg border border-border bg-muted/30 p-4 pl-9 text-muted-foreground text-sm leading-relaxed";

function natureStep(solution: QuadraticSolution, strings: Strings): string {
  const D = solution.integerDiscriminant;
  if (solution.kind === "two-rational") {
    return t(strings.stepPerfectSquare, { root: isqrtBig(D).toString() });
  }
  if (solution.kind === "two-irrational") {
    return strings.stepPositive;
  }
  return solution.kind === "repeated" ? strings.stepZero : strings.stepNegative;
}

function rootsStep(solution: QuadraticSolution, strings: Strings): string {
  if (solution.rationalRoots) {
    const [first, second] = rationalRootWorking(solution);
    return t(strings.stepRoots, {
      roots: t(strings.rootsJoin, {
        first: `x = ${first}`,
        second: `x = ${second}`,
      }),
    });
  }
  const reduced = solution.surd ? formatSurdPair(solution.surd) : "";
  // Nothing cancelled when the reduced denominator is still 2a.
  const cancelled = solution.surd?.s !== BigInt(2) * solution.A;
  const roots = cancelled
    ? `x = ${unreducedSurd(solution)} = ${reduced}`
    : `x = ${reduced}`;
  return t(strings.stepRoots, { roots });
}

export function FormulaSteps({
  solution,
  strings,
}: {
  solution: QuadraticSolution;
  strings: Strings;
}) {
  const { A, B, C } = solution;
  const D = solution.integerDiscriminant;
  const rescaled = !equalsR(solution.multiplier, intR(1));
  const simplifies =
    solution.kind !== "two-rational" &&
    solution.kind !== "repeated" &&
    (solution.kind === "complex" || solution.root.coefficient > BigInt(1));
  return (
    <ol className={STEP_LIST}>
      {rescaled ? (
        <li>
          {t(strings.stepRescale, {
            factor: formatFraction(solution.multiplier),
            equation: formatIntegerQuadratic(A, B, C),
          })}
        </li>
      ) : null}
      <li>
        {t(strings.stepIdentify, {
          a: formatBig(A),
          b: formatBig(B),
          c: formatBig(C),
        })}
      </li>
      <li>
        {t(strings.stepDiscriminant, {
          working: discriminantWorking(A, B, C),
          value: formatBig(D),
        })}
      </li>
      <li>{natureStep(solution, strings)}</li>
      {solution.kind === "repeated" ? (
        <li>
          {t(strings.stepRepeated, { value: rationalRootWorking(solution)[0] })}
        </li>
      ) : (
        <>
          <li>
            {t(strings.stepFormula, {
              substituted: formulaSubstitution(A, B, D),
            })}
          </li>
          {simplifies ? (
            <li>
              {t(strings.stepSimplifyRoot, {
                root: rootSimplification(D, solution.root),
              })}
            </li>
          ) : null}
          <li>{rootsStep(solution, strings)}</li>
        </>
      )}
    </ol>
  );
}

function finalStep(solution: QuadraticSolution, strings: Strings): string {
  const working = squareWorking(solution);
  const roots = solution.rationalRoots;
  if (roots && roots.length === 2) {
    return t(strings.csSolveRational, {
      solution: working.solution,
      first: formatFraction(roots[1]),
      second: formatFraction(roots[0]),
    });
  }
  const value = roots ? formatFraction(roots[0]) : working.solution;
  return t(strings.csSolve, { solution: value });
}

export function SquareSteps({
  solution,
  strings,
}: {
  solution: QuadraticSolution;
  strings: Strings;
}) {
  const w = squareWorking(solution);
  let rootLine = t(strings.csZero, { shifted: w.shifted });
  if (w.rightSign > 0) {
    rootLine = t(strings.csRoot, { shifted: w.shifted, root: w.root });
  } else if (w.rightSign < 0) {
    rootLine = t(strings.csRootComplex, { shifted: w.shifted, root: w.root });
  }
  return (
    <ol className={STEP_LIST}>
      {equalsR(solution.a, intR(1)) ? null : (
        <li>
          {t(strings.csDivide, {
            a: formatFraction(solution.a),
            equation: w.monic,
          })}
        </li>
      )}
      <li>{t(strings.csMove, { left: w.movedLeft, right: w.movedRight })}</li>
      <li>
        {t(strings.csAdd, {
          half: w.half,
          halfSquared: w.halfSquared,
          left: w.addedLeft,
          right: w.right,
        })}
      </li>
      <li>{t(strings.csSquare, { square: w.square, right: w.right })}</li>
      <li>{rootLine}</li>
      <li>{finalStep(solution, strings)}</li>
    </ol>
  );
}
